import React from 'react';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps, ICommand } from '../../../core/tools/interfaces/ITool';
import { useDiagramStore } from '../../../stores/diagramStore';
import { usePerspectiveStore } from '../../../stores/perspectiveStore';
import { BatchUpdatePersonsCommand } from '../commands/BatchUpdatePersonsCommand';
import { Plus, Minus } from 'lucide-react';
import { PaletteMultiSelect } from '../fields/components/PaletteMultiSelect';
import { InheritedTagChips } from '../fields/components/InheritedTagChips';
import { GenogramPerspectiveProjection, SUPPRESSED_INHERITED_HEALTH_KEY } from '../services/GenogramPerspectiveProjection';
import { buildSuppressionUpdates, collectSuppressedTags } from './utils/inheritedTagSuppression';
import { useTranslation } from '@/hooks/useTranslation';
import i18n from '@/i18n/config';
import { bindGenogramToolName } from './utils/toolNameI18n';

const humanizeHealthTagKey = (tag: string): string =>
  tag
    .split('_')
    .map((part) => (part.length ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : ''))
    .join(' ');

type DiagramNodeLike = ReturnType<typeof useDiagramStore.getState>['nodes'][number];

const HealthTagPanel: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const ids = Array.from(context.selection);
  const nodes = useDiagramStore(s => s.nodes);
  const persons = nodes.filter(n => ids.includes(n.id));

  if (persons.length === 0) return null;

  return <HealthTagPanelContent nodes={nodes} persons={persons} onExecute={onExecute} />;
};

const HealthTagPanelContent: React.FC<{
  nodes: DiagramNodeLike[];
  persons: DiagramNodeLike[];
  onExecute: IToolComponentProps['onExecute'];
}> = ({ nodes, persons, onExecute }) => {
  const { t } = useTranslation('genogram');
  const edges = useDiagramStore(s => s.edges);
  const culturePalette = usePerspectiveStore(s => s.culturePalette);
  const healthPalette = usePerspectiveStore(s => s.healthPalette);
  const setHealthItem = usePerspectiveStore(s => s.setHealthItem);
  const [isOpen, setIsOpen] = React.useState(() => {
    try {
      const saved = localStorage.getItem('personPanel.healthOpen');
      return saved ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });

  const toggleSection = () => {
    const next = !isOpen;
    setIsOpen(next);
    try {
      localStorage.setItem('personPanel.healthOpen', JSON.stringify(next));
    } catch {}
  };

  const ensureHealthPaletteItem = (tag: string) => {
    if (!healthPalette[tag]) setHealthItem(tag, { color: '#ef4444', propagate: false });
  };

  const arraysEqual = React.useCallback((a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    return a.every((val, idx) => val === b[idx]);
  }, []);

  const commitTags = React.useCallback((tags: string[]) => {
    if (persons.length === 0) return;
    const sanitized = Array.from(new Set(tags.map(t => t.trim()).filter(Boolean)));
    sanitized.forEach(ensureHealthPaletteItem);

    const batchSpecs = persons.map(p => {
      const existing = ((p as any).attrs?.medicalConditionTags ?? (p as any).medicalConditionTags) as string[] | undefined;
      const current = Array.isArray(existing) ? existing : [];
      if (arraysEqual(current, sanitized)) {
        return null;
      }
      return { nodeId: p.id, changes: { attrs: { medicalConditionTags: sanitized } } };
    }).filter((spec): spec is { nodeId: string; changes: any } => Boolean(spec));

    if (batchSpecs.length === 0) {
      return;
    }

    onExecute([new BatchUpdatePersonsCommand(batchSpecs, `Update health tags for ${batchSpecs.length} person(s)`) ]);
  }, [arraysEqual, ensureHealthPaletteItem, onExecute, persons]);

  const tagSet = new Set<string>();
  persons.forEach(p => (Array.isArray(((p as any).attrs?.medicalConditionTags ?? (p as any).medicalConditionTags)) ? ((p as any).attrs?.medicalConditionTags ?? (p as any).medicalConditionTags) : []).forEach((t: string) => tagSet.add(t)));
  const allTags = Array.from(tagSet);

  const healthById = React.useMemo(
    () => GenogramPerspectiveProjection.compute(nodes as any, edges as any, { culturePalette, healthPalette }).health,
    [nodes, edges, culturePalette, healthPalette],
  );
  const inheritedTags = Array.from(new Set(
    persons.flatMap(p => (healthById.get(p.id) || []).map(entry => entry.tag).filter(tag => !tagSet.has(tag))),
  ));
  const suppressedTags = collectSuppressedTags(persons, SUPPRESSED_INHERITED_HEALTH_KEY).filter(
    tag => !tagSet.has(tag) && !inheritedTags.includes(tag),
  );

  const setInheritedTagSuppressed = (tag: string, suppress: boolean) => {
    const batchSpecs = buildSuppressionUpdates(persons, SUPPRESSED_INHERITED_HEALTH_KEY, tag, suppress);
    if (batchSpecs.length === 0) return;
    onExecute([new BatchUpdatePersonsCommand(batchSpecs, `${suppress ? 'Remove' : 'Restore'} inherited health tag "${tag}" for ${batchSpecs.length} person(s)`)]);
  };

  const healthTagLabel = React.useCallback(
    (tag: string) => t(`settings.healthTagLabels.${tag}` as any, { defaultValue: humanizeHealthTagKey(tag) }),
    [t],
  );

  const multiSelectOptions = React.useMemo(() => {
    const optionMap = new Map<string, { value: string; label: string; color?: string; icon?: string }>();
    Object.entries(healthPalette).forEach(([tag, meta]) => {
      optionMap.set(tag, { value: tag, label: healthTagLabel(tag), color: meta.color, icon: meta.icon });
    });

    allTags.forEach(tag => {
      if (!optionMap.has(tag)) {
        optionMap.set(tag, { value: tag, label: healthTagLabel(tag) });
      }
    });
    return Array.from(optionMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [healthPalette, allTags, healthTagLabel]);

  return (
    <>
      <div className="h-px bg-border my-4" />
      <div className="px-3 pr-6">
        <button
          type="button"
          onClick={toggleSection}
          className="flex items-center justify-between w-full mb-3 group"
        >
          <h3 className="text-[11px] font-semibold text-foreground uppercase tracking-wider">
            {t('medical.healthTitle', 'Health')}
          </h3>
          <div className="opacity-70 group-hover:opacity-100 transition-opacity">
            {isOpen ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          </div>
        </button>

        {isOpen && (
          <div className="space-y-3">
            <PaletteMultiSelect
              id="health-tags"
              value={allTags}
              options={multiSelectOptions}
              placeholder={t('medical.selectHealthConditions', 'Select health conditions')}
              allowCustomOptions
              onCommit={commitTags}
            />
            <InheritedTagChips
              testIdPrefix="tid.genogram.health"
              inheritedTags={inheritedTags}
              suppressedTags={suppressedTags}
              getLabel={healthTagLabel}
              getColor={tag => healthPalette[tag]?.color}
              onRemove={tag => setInheritedTagSuppressed(tag, true)}
              onRestore={tag => setInheritedTagSuppressed(tag, false)}
            />
          </div>
        )}
      </div>
    </>
  );
};

export class HealthTagTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'genogram-health-tags',
    name: i18n.t('genogram:tools.healthTags', { defaultValue: 'Health Tags' }) as string,
    icon: 'activity',
    category: 'edit',
    description: 'Manage health conditions, colors, icons, and propagation',
    version: '1.0.0',
    supportedDiagramTypes: ['genogram'],
    requiresSelection: true,
    minSelection: 1,
    ui: { toolbar: false, contextMenu: false, propertyPanel: true, sidebar: false, floatingPanel: false, statusBar: false },
    capabilities: { modifiesShapes: true, createsShapes: false, deletesShapes: false, modifiesConnectors: false, createsConnectors: false, deletesConnectors: false, requiresUserInput: true, supportsMultiSelect: true, supportsBatchOperations: true }
  };

  constructor() {
    super();
    bindGenogramToolName(this.manifest, 'tools.healthTags', 'Health Tags');
  }

  canExecute(context: IToolContext): boolean {
    if (context.diagramType !== 'genogram') return false;
    const ids = Array.from(context.selection);
    if (ids.length === 0) return false;
    const nodes = useDiagramStore.getState().nodes.filter(n => ids.includes(n.id));
    if (nodes.length === 0) return false;
    return nodes.every(n => n.type === 'person');
  }
  execute(params?: unknown): ICommand | ICommand[] | null { return Array.isArray(params) ? params as ICommand[] : null; }
  getPropertyPanelComponent() { return HealthTagPanel; }
}
